import { createApi } from '@reduxjs/toolkit/query/react';

import { createBaseQuery } from '../../common/api/createBaseQuery';

import { type IAppContext } from '../../common/types/applicationContext';
import {
  fetchPublicB2cV1GetOffers,
  TPublicB2cV1GetOffersRequest,
  TPublicB2cV1GetOffersResponse_1 as TPublicB2cV1GetOffersResponse,
} from '../../common/repositories/mortgage-broker-calculator/b2c/v1/get-offers';

import {
  fetchPublicB2cV2GetOffers,
  TPublicB2cV2GetOffersRequest,
  TPublicB2cV2GetOffersResponse_1 as TPublicB2cV2GetOffersResponse,
} from '../../common/repositories/mortgage-broker-calculator/b2c/v2/get-offers';

import {
  fetchPublicB2cV3GetOffers,
  TPublicB2cV3GetOffersRequest,
  TPublicB2cV3GetOffersResponse_1 as TPublicB2cV3GetOffersResponse,
} from 'shared/common/repositories/mortgage-broker-calculator/b2c/v3/get-offers';

import {
  fetchPublicB2cV1SaveOffersFromListing,
  TPublicB2cV1SaveOffersFromListingRequest,
  TPublicB2cV1SaveOffersFromListingResponse_1 as TPublicB2cV1SaveOffersFromListingResponse,
} from '../../common/repositories/mortgage-broker-calculator/b2c/v1/save-offers-from-listing';

import { ERuntimeSettings } from '../../common/types/runtimeSettings';

export type TMortgageMiniCalculatorApiEndpoints = keyof typeof mortgageMiniCalculatorApi.endpoints;
export let mortgageMiniCalculatorApi: ReturnType<typeof createMortgageMiniCalculatorApi>;

export const createMortgageMiniCalculatorApi = (ctx: IAppContext) => {
  const isEnableGetOffersV3 = ctx.config.getStrict(ERuntimeSettings.enableGetOffersV3);
  const newApi = createApi({
    reducerPath: 'mortgageMiniCalculatorApi',
    baseQuery: createBaseQuery(ctx),
    endpoints: builder => ({
      getOffers: builder.query<
        TPublicB2cV1GetOffersResponse | TPublicB2cV2GetOffersResponse | TPublicB2cV3GetOffersResponse,
        TPublicB2cV1GetOffersRequest | TPublicB2cV2GetOffersRequest | TPublicB2cV3GetOffersRequest
      >({
        query: parameters => {
          // Для v3 API нужны objectType и realEstate а также флаг isEnableGetOffersV3
          if (
            isEnableGetOffersV3 &&
            'objectType' in parameters &&
            parameters.objectType &&
            'realEstate' in parameters &&
            parameters.realEstate
          ) {
            return {
              parameters,
              fetchFunction: fetchPublicB2cV3GetOffers,
              loggerInfo: {
                description: 'Fetching offers for mini calculator (v3)',
                degradation: 'Critical error display, listing unavailable (v3)',
              },
            };
          }
          // TODO: после стабилизации v3 удалить поддержку v1 и v2 https://jira.cian.tech/browse/CD-236273
          // Для v2 API нужны objectType и realEstate
          if (
            'objectType' in parameters &&
            parameters.objectType &&
            'realEstate' in parameters &&
            parameters.realEstate
          ) {
            return {
              parameters,
              fetchFunction: fetchPublicB2cV2GetOffers,
              loggerInfo: {
                description: 'Fetching offers for mini calculator (v2)',
                degradation: 'Critical error display, listing unavailable (v2)',
              },
            };
          } else {
            return {
              parameters,
              fetchFunction: fetchPublicB2cV1GetOffers,
              loggerInfo: {
                description: 'Fetching offers for mini calculator (v1)',
                degradation: 'Critical error display, listing unavailable (v1)',
              },
            };
          }
        },
      }),
      saveOffersFromListing: builder.mutation<
        TPublicB2cV1SaveOffersFromListingResponse,
        TPublicB2cV1SaveOffersFromListingRequest
      >({
        query: parameters => {
          return {
            parameters,
            fetchFunction: fetchPublicB2cV1SaveOffersFromListing,
            loggerInfo: {
              description: 'Saving offers from listing',
              degradation: 'Failed to save offers from listing',
            },
          };
        },
      }),
    }),
  });

  mortgageMiniCalculatorApi = newApi;

  return newApi;
};
